import cfg    from '../config.json';
import labels from '../labels.json';
import msgs   from '../messages.json';
import { getLang, apply as applyLang, toggleLang } from './i18n.js';

export class UI {
  constructor() {
    this.dropzone        = document.getElementById('dropzone');
    this.fileInput       = document.getElementById('file-input');
    this.processingView  = document.getElementById('processing-view');
    this.outputCanvas    = document.getElementById('output-canvas');
    this.noFacesOverlay  = document.getElementById('no-faces-overlay');
    this.progressBar     = document.getElementById('progress-bar');
    this.progressLabel   = document.getElementById('progress-label');
    this.progressWrapper = document.getElementById('progress-wrapper');
    this.stats           = document.getElementById('stats');
    this.btnNew      = document.getElementById('btn-new');
    this.btnDownload = document.getElementById('btn-download');
    this.statusBar       = document.getElementById('status-bar');

    this._inputs = {}; // key → input element
    applyLang(getLang());
    document.getElementById('btn-lang').addEventListener('click', toggleLang);
    if (this._checkSupport()) this._buildSettings();
  }

  // ── Browser support check ──────────────────────────────────────────────

  _checkSupport() {
    const reasons = [];
    if (typeof WebAssembly === 'undefined') reasons.push('wasm');

    const hasMediaRecorder = 'captureStream' in HTMLCanvasElement.prototype &&
                             typeof MediaRecorder !== 'undefined';
    const hasWebCodecs = typeof VideoEncoder !== 'undefined' &&
                         typeof VideoFrame   !== 'undefined';
    if (!hasMediaRecorder && !hasWebCodecs) reasons.push('encoding');

    if (reasons.length === 0) return true;

    const m = msgs[getLang()].unsupported;
    document.getElementById('dropzone').classList.add('hidden');
    document.getElementById('info-intro').classList.add('hidden');
    document.getElementById('info-tech').classList.add('hidden');

    const box = document.getElementById('unsupported');
    document.getElementById('unsupported-title').textContent = m.title;
    document.getElementById('unsupported-reasons').innerHTML =
      reasons.map(k => `<li>${m[k]}</li>`).join('');
    document.getElementById('unsupported-hint').textContent = m.hint;
    box.classList.remove('hidden');

    return false;
  }

  // ── Dynamic settings panel ─────────────────────────────────────────────

  _buildSettings() {
    const aside = document.getElementById('settings');
    const m = msgs[getLang()].settings;

    const h2 = document.createElement('h2');
    h2.dataset.i18n = 'settings.title';
    h2.textContent = m.title;
    aside.appendChild(h2);

    const note = document.createElement('p');
    note.className = 'settings-note';
    note.dataset.i18n = 'settings.note';
    note.textContent = m.note;
    aside.appendChild(note);

    const startBtn = document.getElementById('btn-start');
    if (startBtn) {
      startBtn.dataset.i18n = 'settings.startButton';
      startBtn.textContent = m.startButton;
    }

    for (const section of labels.settings.sections) {
      const sec = document.createElement('div');
      sec.className = 'settings-section';

      const title = document.createElement('h3');
      title.className = 'settings-section-title';
      title.dataset.i18n = `settings.sections.${section.key}`;
      title.textContent = m.sections[section.key];
      sec.appendChild(title);

      for (const field of section.fields) {
        sec.appendChild(this._buildField(field, m.fields[field.key]));
      }

      aside.appendChild(sec);
    }
  }

  _buildField(field, fm) {
    const wrap = document.createElement('div');
    wrap.className = 'setting-field';

    const header = document.createElement('div');
    header.className = 'setting-header';

    const lbl = document.createElement('label');
    lbl.dataset.i18n = `settings.fields.${field.key}.label`;
    lbl.textContent = fm.label;
    lbl.htmlFor = `sf-${field.key}`;
    header.appendChild(lbl);

    if (field.recommended != null) {
      const rec = document.createElement('span');
      rec.className = 'setting-recommended';
      rec.title = 'Click to reset';
      rec.textContent = `rec. ${field.recommended}`;
      rec.addEventListener('click', () => {
        const inp = this._inputs[field.key];
        if (!inp) return;
        inp.value = field.recommended;
        inp.dispatchEvent(new Event('input'));
      });
      header.appendChild(rec);
    }
    wrap.appendChild(header);

    const desc = document.createElement('p');
    desc.className = 'setting-description';
    desc.dataset.i18n = `settings.fields.${field.key}.description`;
    desc.textContent = fm.description;
    wrap.appendChild(desc);

    const ctrl = document.createElement('div');
    ctrl.className = 'setting-control';

    const defaultVal = this._cfgValue(field.configPath);

    if (field.type === 'range') {
      const inp = document.createElement('input');
      inp.type = 'range'; inp.id = `sf-${field.key}`;
      inp.min = field.min; inp.max = field.max; inp.step = field.step;
      inp.value = defaultVal;

      const decimals = field.step < 1 ? String(field.step).split('.')[1]?.length ?? 0 : 0;
      const fmt = v => parseFloat(v).toFixed(decimals);

      const val = document.createElement('span');
      val.className = 'setting-value';
      val.textContent = fmt(defaultVal);

      inp.addEventListener('input', () => {
        val.textContent = fmt(inp.value);
        val.classList.toggle('modified',
          field.recommended != null && Math.abs(parseFloat(inp.value) - field.recommended) > field.step / 2);
      });

      ctrl.appendChild(inp); ctrl.appendChild(val);
      this._inputs[field.key] = inp;

    } else if (field.type === 'select') {
      const sel = document.createElement('select');
      sel.id = `sf-${field.key}`;
      for (const opt of field.options) {
        const o = document.createElement('option');
        o.value = opt.value;
        o.dataset.i18n = `settings.fields.${field.key}.options.${opt.value}`;
        o.textContent = fm.options[opt.value];
        if (opt.value === defaultVal) o.selected = true;
        sel.appendChild(o);
      }
      ctrl.appendChild(sel);
      this._inputs[field.key] = sel;
    }

    wrap.appendChild(ctrl);
    return wrap;
  }

  _cfgValue(path) {
    return path.split('.').reduce((o, k) => o?.[k], cfg);
  }

  get settings() {
    const num = key => parseFloat(this._inputs[key]?.value ?? 0);
    const str = key => this._inputs[key]?.value ?? '';
    const radius = num('blurRadius');
    return {
      threshold:           num('threshold'),
      blurRadius:          radius,
      sigma:               radius / 2,
      pixelate:            str('blurStyle') === 'pixelate',
      pixelSize:           cfg.blur.pixelSize,
      maxGapFrames:        num('maxGapFrames'),
      gapFillMinScore:     num('gapFillMinScore'),
      persistenceFrames:   num('persistenceFrames'),
      persistenceMinScore: num('persistenceMinScore'),
    };
  }

  onFileSelected(callback) {
    this.dropzone.addEventListener('click', () => this.fileInput.click());
    this.dropzone.addEventListener('keydown', e => { if (e.key === 'Enter') this.fileInput.click(); });
    this.fileInput.addEventListener('change', e => {
      const file = e.target.files?.[0];
      if (file) callback(file);
    });

    // Drag & drop
    this.dropzone.addEventListener('dragover', e => {
      e.preventDefault();
      this.dropzone.classList.add('drag-over');
    });
    this.dropzone.addEventListener('dragleave', () => this.dropzone.classList.remove('drag-over'));
    this.dropzone.addEventListener('drop', e => {
      e.preventDefault();
      this.dropzone.classList.remove('drag-over');
      const file = e.dataTransfer.files?.[0];
      if (file?.type.startsWith('video/')) callback(file);
    });
  }

  onNewVideo(callback)  { document.getElementById('btn-new').addEventListener('click', callback); }
  onStart(callback)     { document.getElementById('btn-start').addEventListener('click', callback); }

  /** Step 2 — show canvas with first frame + settings + start button. */
  showConfirmStep(width, height) {
    this.dropzone.classList.add('hidden');
    document.getElementById('info-intro').classList.add('hidden');
    document.getElementById('info-tech').classList.add('hidden');
    this.processingView.classList.remove('hidden');
    document.getElementById('settings').classList.remove('hidden');
    document.getElementById('confirm-bar').classList.remove('hidden');
    document.getElementById('main').classList.add('confirm');
    document.getElementById('progress-wrapper').classList.add('hidden');
    document.getElementById('review-panel').classList.add('hidden');
    this.outputCanvas.width  = width;
    this.outputCanvas.height = height;
    this.btnDownload.classList.add('hidden');
    this.btnDownload.disabled = true;
    this.btnNew.classList.remove('hidden');
    this.noFacesOverlay.classList.add('hidden');
  }

  /** Step 3 — hide settings + confirm bar, reveal progress bar. */
  startProcessing() {
    document.getElementById('settings').classList.add('hidden');
    document.getElementById('confirm-bar').classList.add('hidden');
    document.getElementById('main').classList.remove('confirm');
    document.getElementById('progress-wrapper').classList.remove('hidden');
  }

  showDropzone() {
    this.processingView.classList.add('hidden');
    document.getElementById('settings').classList.add('hidden');
    document.getElementById('confirm-bar').classList.add('hidden');
    document.getElementById('main').classList.remove('confirm');
    this.dropzone.classList.remove('hidden');
    document.getElementById('info-intro').classList.remove('hidden');
    document.getElementById('info-tech').classList.remove('hidden');
    this.btnNew.classList.add('hidden');
    this.btnDownload.classList.add('hidden');
    this.fileInput.value = '';
    this.setStatus('');
  }

  renderFrame(imageData) {
    this.outputCanvas.getContext('2d').putImageData(imageData, 0, 0);
  }

  updateProgress(index, total, facesFound, msDetect, msBlur) {
    const pct = total > 0 ? (index / total) * 100 : 0;
    this.progressBar.style.width = `${pct.toFixed(1)}%`;
    this.progressLabel.textContent = `${index} / ${total} frames`;
    const fps = msDetect + msBlur > 0 ? (1000 / (msDetect + msBlur)).toFixed(1) : '—';
    this.stats.textContent =
      `Faces: ${facesFound}  ·  Detect: ${msDetect.toFixed(0)} ms  ·  Blur: ${msBlur.toFixed(0)} ms  ·  ${fps} fps`;
  }

  setNoFacesOverlay(visible) {
    this.noFacesOverlay.classList.toggle('hidden', !visible);
  }

  enableDownload(url, filename) {
    this.btnDownload.classList.remove('hidden');
    this.btnDownload.disabled = false;
    this.btnDownload.onclick = () => {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
    };
  }

  // ── Review mode ────────────────────────────────────────────────────────

  showReviewPanel(totalFrames) {
    this._reviewTotal = totalFrames;
    const panel   = document.getElementById('review-panel');
    const scrubber = document.getElementById('frame-scrubber');
    scrubber.max   = totalFrames - 1;
    scrubber.value = 0;
    document.getElementById('frame-counter').textContent = `0 / ${totalFrames - 1}`;
    panel.classList.remove('hidden');
  }

  onScrub(callback) {
    const scrubber  = document.getElementById('frame-scrubber');
    const btnPrev   = document.getElementById('btn-prev-frame');
    const btnNext   = document.getElementById('btn-next-frame');

    const fire = () => callback(parseInt(scrubber.value, 10));

    scrubber.addEventListener('input', fire);
    btnPrev.addEventListener('click', () => {
      scrubber.value = Math.max(0, parseInt(scrubber.value, 10) - 1);
      fire();
    });
    btnNext.addEventListener('click', () => {
      scrubber.value = Math.min(this._reviewTotal - 1, parseInt(scrubber.value, 10) + 1);
      fire();
    });
  }

  updateReviewScrubber(frameIndex) {
    const scrubber = document.getElementById('frame-scrubber');
    scrubber.value = frameIndex;
    document.getElementById('frame-counter').textContent =
      `${frameIndex} / ${this._reviewTotal - 1}`;
  }

  updateDetectionInfo(frameIndex, detections) {
    document.getElementById('frame-counter').textContent =
      `${frameIndex} / ${this._reviewTotal - 1}`;

    const el = document.getElementById('detection-info');
    if (!detections.length) {
      el.innerHTML = '<span class="det-badge none">no faces</span>';
      return;
    }
    el.innerHTML = detections.map((d, i) => {
      const cls   = d.ghost === 'persist' ? 'persist' : d.ghost === 'gap' ? 'gap' : 'real';
      const label = d.ghost === 'persist' ? '↔ persisted'
                  : d.ghost === 'gap'     ? '⚬ interpolated'
                  :                         '● detected';
      const score = (d.score * 100).toFixed(0);
      const box   = `[${Math.round(d.x1)}, ${Math.round(d.y1)}, ${Math.round(d.x2)}, ${Math.round(d.y2)}]`;
      return `<span class="det-badge ${cls}" title="${box}">face ${i + 1} · ${score}% · ${label}</span>`;
    }).join('');
  }

  setStatus(message, { loading = false, error = false } = {}) {
    this.statusBar.textContent = message;
    this.statusBar.className = 'status-bar' +
      (loading ? ' loading' : '') +
      (error   ? ' error'   : '');
  }
}
